import React, { useState, useContext } from 'react';
import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/logeduser';
import { useCompanyModules, isModuleEnabled } from '../../context/CompanyModulesContext';
import { canSeeShiftManagement } from '../../components/Attendance/ShiftManagement/shiftManagementAccess';

// Icons
import HomeIcon from '@mui/icons-material/HomeOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import MemoryIcon from '@mui/icons-material/MemoryOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import PersonOffIcon from '@mui/icons-material/PersonOffOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeatOutlined';
import RestoreIcon from '@mui/icons-material/RestoreOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import FilePresentIcon from '@mui/icons-material/FilePresentOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAltOutlined';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import Diversity3Icon from '@mui/icons-material/Diversity3Outlined';
import PersonIcon from '@mui/icons-material/PersonOutlined';
import CalculateIcon from '@mui/icons-material/CalculateOutlined';
import WorkIcon from '@mui/icons-material/WorkOutline';

// `open` is forwarded to the underlying Drawer (needed by the temporary mobile
// variant for visibility) AND consumed by the style fn below for the 88↔280 width.
const StyledDrawer = styled(Drawer)(
  ({ theme, open }) => ({
    width: open ? 280 : 88,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    zIndex: 1300, // Above the fixed header
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '& .MuiDrawer-paper': {
      width: open ? 280 : 88,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      overflowX: 'hidden',
      backgroundColor: '#fff',
      borderRight: '1px solid #F3F4F6',
      boxShadow: open ? '8px 0 32px rgba(0,0,0,0.04)' : 'none',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      marginTop: '64px',
      height: 'calc(100vh - 64px)',
      borderRadius: 0, // Ensure it matches the flat bottom of the navbar
    },
  })
);

const SectionHeader = ({ title, open, pt = 3 }) => (
  <Typography
    variant="caption"
    sx={{
      px: 3,
      pt,
      pb: 1,
      display: 'block',
      color: '#9CA3AF',
      fontWeight: 700,
      letterSpacing: '0.1em',
      fontSize: '0.6rem',
      opacity: open ? 0.7 : 0,
      transition: 'opacity 0.2s ease',
    }}
  >
    {title}
  </Typography>
);

const Sd = ({ isDrawerOpen, setIsDrawerOpen }) => {
  const { authData } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // Below md the rail becomes an off-canvas overlay toggled by the header
  // hamburger (no hover-expand): fully expanded when shown, hidden otherwise.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Desktop: the rail is FIXED open at full width — no hover-expand/collapse.
  // Mobile: still toggled as an off-canvas overlay via the header hamburger.
  const drawerOpen = isMobile ? isDrawerOpen : true;

  // Sub-menu states
  const [openMenus, setOpenMenus] = useState({
    employees: false,
    payroll: false,
    leaves: false,
    candidates: false,
    settings: false,
    calculator: false,
    form16: false,
  });

  const toggleMenu = (menu) => {
    if (!drawerOpen) return;
    setOpenMenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
  };

  const handleNavigation = (route) => {
    navigate(route);
    if (isMobile) setIsDrawerOpen(false);          // close the off-canvas overlay
  };

  const isSelected = (route) => location.pathname === route;

  const NavItem = ({ icon, label, route, onClick, hasSubmenu, isOpen }) => {
    const active = isSelected(route);
    return (
      <ListItemButton
        onClick={onClick || (() => handleNavigation(route))}
        sx={{
          mx: 1.5,
          my: 0.3,
          borderRadius: '12px',
          minHeight: 44,
          justifyContent: drawerOpen ? 'initial' : 'center',
          px: 2,
          // Soft-blue pill for the active item — matches the employee rail exactly.
          backgroundColor: active ? '#eef4ff' : 'transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: active ? '#eef4ff' : '#f1f5f9',
            '& .MuiListItemIcon-root': { color: '#2f6bff' },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: drawerOpen ? 2 : 'auto',
            justifyContent: 'center',
            color: active ? '#2f6bff' : '#6B7280',
            transition: 'color 0.2s ease',
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: '1.25rem' } })}
        </ListItemIcon>
        {drawerOpen && (
          <>
            <ListItemText
              primary={label}
              primaryTypographyProps={{
                fontSize: '0.92rem',
                fontWeight: active ? 700 : 600,
                color: active ? '#2f6bff' : '#475569',
              }}
            />
            {hasSubmenu && (
              <Box sx={{ ml: 'auto', display: 'flex' }}>
                {isOpen ? <ExpandLess sx={{ fontSize: '1rem', color: '#9CA3AF' }} /> : <ExpandMore sx={{ fontSize: '1rem', color: '#9CA3AF' }} />}
              </Box>
            )}
          </>
        )}
      </ListItemButton>
    );
  };

  const { modules: companyModules } = useCompanyModules();

  const hasPermission = (module, action = 'view') => {
    if (!isModuleEnabled(companyModules, module)) return false;
    if (!authData?.admin?.is_restricted) return true;
    return authData?.admin?.permissions?.[module]?.[action] === true;
  };
  const isGlobalAdmin = !authData?.admin?.is_restricted &&
    authData?.admin?.email === 'admin@atshub.in';

  return (
    <StyledDrawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? isDrawerOpen : drawerOpen}
      onClose={() => setIsDrawerOpen(false)}
      ModalProps={{ keepMounted: true }}
    >
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        pt: 1,
        pb: 2.5,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha('#2f6bff', 0.2),
          borderRadius: '10px',
        },
        '&:hover::-webkit-scrollbar-thumb': {
          background: alpha('#2f6bff', 0.5),
          borderRadius: '10px',
        }
      }}>
        <List disablePadding>
          <SectionHeader title="MANAGEMENT" open={drawerOpen} pt={1} />
          {hasPermission('dashboard') && <NavItem icon={<HomeIcon />} label="Dashboard" route="/dashboard" />}

          {hasPermission('employees') && (
            <>
              <NavItem
                icon={<PeopleIcon />}
                label="Employees"
                hasSubmenu
                isOpen={openMenus.employees}
                onClick={() => toggleMenu('employees')}
              />
              <Collapse in={openMenus.employees && drawerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 0.5 }}>
                  {hasPermission('employees', 'edit') && <NavItem icon={<PersonIcon />} label="Add New Employee" route="/createemployee" />}
                  <NavItem icon={<AssignmentIcon />} label="Onboarding" route="/onboarding" />
                  <NavItem icon={<FilePresentIcon />} label="Documents" route="/uploademployee/docs" />
                  {hasPermission('employees', 'edit') && <NavItem icon={<GroupIcon />} label="Bulk Action" route="/uploaddata" />}
                  <NavItem icon={<PeopleIcon />} label="Total Employee" route="/employees" />
                  <NavItem icon={<RestoreIcon />} label="Inactive" route="/resigned/employees" />
                </List>
              </Collapse>
            </>
          )}

          {isModuleEnabled(companyModules, 'calculator') && (
            <>
              <NavItem
                icon={<CalculateIcon />}
                label="Calculator"
                hasSubmenu
                isOpen={openMenus.calculator}
                onClick={() => toggleMenu('calculator')}
              />
              <Collapse in={openMenus.calculator && drawerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 0.5 }}>
                  <NavItem icon={<CalculateIcon />} label="Income Tax Calculator" route="/tax-calculator" />
                </List>
              </Collapse>
            </>
          )}

          {hasPermission('payroll') && (
            <>
              <NavItem
                icon={<AccountBalanceIcon />}
                label="Payroll"
                hasSubmenu
                isOpen={openMenus.payroll}
                onClick={() => toggleMenu('payroll')}
              />
              <Collapse in={openMenus.payroll && drawerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 0.5 }}>
                  <NavItem icon={<CurrencyRupeeIcon />} label="Salary View" route="/payroll" />
                  {hasPermission('payroll', 'edit') && <NavItem icon={<MemoryIcon />} label="Processing" route="/payroll/process" />}
                  {hasPermission('payroll', 'edit') && <NavItem icon={<CloudUploadIcon />} label="Bulk Updates" route="/payroll/bulk-salary" />}
                  <NavItem icon={<ReceiptLongIcon />} label="Pay Slip Logs" route="/payroll/logs" />
                  <NavItem icon={<AssignmentIcon />} label="Detailed Salary Report" route="/payroll/detailed-salary-report" />
                </List>
              </Collapse>
            </>
          )}

          {hasPermission('payroll') && (
            <>
              <NavItem
                icon={<AssignmentIcon />}
                label="Form 16"
                hasSubmenu
                isOpen={openMenus.form16}
                onClick={() => toggleMenu('form16')}
              />
              <Collapse in={openMenus.form16 && drawerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 0.5 }}>
                  <NavItem icon={<AssignmentIcon />} label="Form 16 Certificates" route="/payroll/form16" />
                  <NavItem icon={<AssignmentIcon />} label="Tax Declarations" route="/payroll/form16/declarations" />
                  <NavItem icon={<AssignmentIcon />} label="Rectifications" route="/payroll/form16/rectifications" />
                  {hasPermission('payroll', 'edit') && <NavItem icon={<AccountBalanceIcon />} label="Employer / FY Setup" route="/payroll/form16/employer" />}
                </List>
              </Collapse>
            </>
          )}

          {hasPermission('recruitment') && (
            <>
              <SectionHeader title="RECRUITMENT" open={drawerOpen} />
              <NavItem
                icon={<GroupIcon />}
                label="Interview"
                hasSubmenu
                isOpen={openMenus.candidates}
                onClick={() => toggleMenu('candidates')}
              />
              <Collapse in={openMenus.candidates && drawerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 0.5 }}>
                  {hasPermission('recruitment', 'edit') && <NavItem icon={<WorkIcon />} label="Job Openings" route="/job-openings" />}
                  {hasPermission('recruitment', 'edit') && <NavItem icon={<WorkIcon />} label="Job Applications" route="/job-applications" />}
                  <NavItem icon={<PersonIcon />} label="Total Applies" route="/totalcandidates" />
                  <NavItem icon={<ScheduleIcon />} label="Scheduled" route="/scheduledcandidates" />
                  <NavItem icon={<PersonIcon />} label="Selected" route="/selectedcandidates" />
                  <NavItem icon={<ThumbDownOffAltIcon />} label="Rejected" route="/rejectedcandidates" />
                  <NavItem icon={<EventRepeatIcon />} label="On Process" route="/progresscandidates" />
                </List>
              </Collapse>
            </>
          )}

          <SectionHeader title="PEOPLE" open={drawerOpen} />
          {isModuleEnabled(companyModules, 'projects') && (
            <NavItem icon={<AssignmentIcon />} label="Projects" route="/projects" />
          )}
          {isModuleEnabled(companyModules, 'contacts') && (
            <NavItem icon={<ContactsIcon />} label="Contacts" route="/contacts" />
          )}
          {hasPermission('leaves') && (
            <>
              <NavItem
                icon={<PersonOffIcon />}
                label="Attendance"
                hasSubmenu
                isOpen={openMenus.leaves}
                onClick={() => toggleMenu('leaves')}
              />
              <Collapse in={openMenus.leaves && drawerOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 0.5 }}>
                  <NavItem icon={<EventRepeatIcon />} label="Leave Requests" route="/leaves" />
                  <NavItem icon={<PersonIcon />} label="Daily Status" route="/attendance/present" />
                  {canSeeShiftManagement(authData) && <NavItem icon={<ScheduleIcon />} label="Shift Management" route="/attendance/shift-management" />}
                  {canSeeShiftManagement(authData) && <NavItem icon={<MemoryIcon />} label="Biometric Management" route="/attendance/biometric" />}
                  <NavItem icon={<Diversity3Icon />} label="Team Analysis" route="/team-leaves" />
                  <NavItem icon={<ScheduleIcon />} label="Tracker" route="/leave-tracker" />
                </List>
              </Collapse>
            </>
          )}

          <SectionHeader title="PREFERENCES" open={drawerOpen} />
          <NavItem
            icon={<SettingsIcon />}
            label="Global Settings"
            hasSubmenu
            isOpen={openMenus.settings}
            onClick={() => toggleMenu('settings')}
          />
          <Collapse in={openMenus.settings && drawerOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 0.5 }}>
              <NavItem icon={<PersonIcon />} label="My Profile" route="/settings" />
              {hasPermission('master_records') && <NavItem icon={<StorageIcon />} label="Master Records" route="/master-data" />}
              {hasPermission('settings', 'edit') && <NavItem icon={<SettingsIcon />} label="Company Settings" route="/settings/company-profile" />}
              {hasPermission('settings') && <NavItem icon={<GroupIcon />} label="User Role Management" route="/settings/roles" />}
              {isGlobalAdmin && <NavItem icon={<HistoryIcon />} label="Activity Logs" route="/settings/activity-logs" />}
            </List>
          </Collapse>
        </List>
      </Box>

      {/* User Profile Footer */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid #F3F4F6',
        backgroundColor: '#fff'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: drawerOpen ? 'flex-start' : 'center',
          gap: 1.5,
          overflow: 'hidden',
          transition: 'all 0.2s ease',
        }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: '#2f6bff',
              fontSize: '0.9rem',
              fontWeight: 700,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)'
            }}
          >
            {authData?.admin?.user_name?.charAt(0).toUpperCase() || 'A'}
          </Avatar>
          {drawerOpen && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827', noWrap: true, lineHeight: 1.2 }}>
                {authData?.admin?.user_name || 'Admin User'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9CA3AF', noWrap: true, display: 'block' }}>
                {authData?.admin?.email || 'admin@atshub.in'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </StyledDrawer>
  );
};

export default Sd;
